"use client";

/**
 * Brand 페이지 클라이언트 컨텐츠
 *
 * 브랜드 프로필 + 상품 그리드 + 정렬 + 페이지네이션
 * 패턴: WishlistContent / ShoppingContent와 일관
 */
import { useEffect, useState, type FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { PackageOpen, Search } from "lucide-react";

import { trackViewBrand } from "@/shared/analytics";
import type { ProductListItem } from "@/shared/api/services/productService";
import { useAuth } from "@/shared/auth/hooks";
import { usePagination } from "@/shared/hooks/usePagination";
import { EmptyState } from "@/shared/ui/EmptyState";
import { cn } from "@/lib/utils";

import { useBrandInfo, useBrandProducts } from "@/features/brand/queries/useBrandProducts";
import { ProductCard } from "@/features/shopping/components/ProductCard";
import { ProductListSkeleton } from "@/features/shopping/components/ProductListSkeleton";

import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const SORT_OPTIONS = [
  { value: "best", label: "인기순" },
  { value: "recommend", label: "추천순" },
  { value: "latest", label: "최신순" },
  { value: "price_asc", label: "가격 낮은순" },
  { value: "price_desc", label: "가격 높은순" },
] as const;

const PRICE_SORT_VALUES = new Set(["price_asc", "price_desc"]);

const PRICE_RANGES = [
  { label: "~10,000원", min: 0, max: 10000, value: "0-10000" },
  { label: "10,000~30,000원", min: 10000, max: 30000, value: "10000-30000" },
  { label: "30,000~50,000원", min: 30000, max: 50000, value: "30000-50000" },
  { label: "50,000원~", min: 50000, max: Infinity, value: "50000-" },
] as const;

interface BrandContentProps {
  brandId: string;
}

function BrandProfileSkeleton() {
  return (
    <div className="mb-8">
      <Skeleton className="h-40 w-full rounded-md" />
      <div className="mt-4 flex items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />
        <Skeleton className="h-6 w-40" />
      </div>
    </div>
  );
}

function BrandProfile({ brandId }: { brandId: string }) {
  const { data, isLoading } = useBrandInfo(brandId);
  const { user } = useAuth();
  const brand = data?.data;
  const brandName = brand?.name;
  const numericBrandId = brand ? Number(brandId) : undefined;

  useEffect(() => {
    if (!brandName || numericBrandId == null) return;
    trackViewBrand({
      userId: user?.id,
      brandId: numericBrandId,
      brandName,
    });
  }, [brandName, numericBrandId, user?.id]);

  if (isLoading) return <BrandProfileSkeleton />;
  if (!brand) return null;

  const discountRate = Number(brand.discountRate ?? 0);
  const minOrder = brand.minimumOrderValue ?? 0;

  const hasInfo = (discountRate > 0 && minOrder > 0) || brand.basicShippingFee > 0;

  return (
    <div className="mb-8">
      {/* Background banner */}
      {brand.backgroundImageUrl && (
        <div className="relative h-48 md:h-56 overflow-hidden rounded-md bg-neutral-100">
          <img
            src={brand.backgroundImageUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          {brand.logoImageUrl && (
            <div className="absolute bottom-4 left-4 flex items-center gap-3">
              <div className="h-14 w-14 overflow-hidden rounded-full border-2 border-white bg-white shadow-md relative">
                <img
                  src={brand.logoImageUrl}
                  alt={brand.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white drop-shadow-sm">{brand.name}</h1>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header for brands without banner */}
      {!brand.backgroundImageUrl && (
        <div className="border-b border-neutral-200 pb-5">
          <h1 className="text-2xl font-bold tracking-tight">{brand.name}</h1>
          {hasInfo && (
            <div className="mt-2 flex flex-wrap items-center gap-2.5 text-sm text-neutral-600">
              {discountRate > 0 && minOrder > 0 && (
                <>
                  <span className="rounded bg-neutral-900 px-1.5 py-0.5 text-xs font-medium text-white">
                    할인
                  </span>
                  <span>
                    {minOrder.toLocaleString()}원 이상{" "}
                    <span className="font-bold text-neutral-900">{Math.floor(discountRate)}%</span>
                  </span>
                </>
              )}
              {discountRate > 0 && minOrder > 0 && brand.basicShippingFee > 0 && (
                <span className="text-neutral-300">|</span>
              )}
              {brand.basicShippingFee > 0 && (
                <span>배송비 {brand.basicShippingFee.toLocaleString()}원</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Brand info below banner */}
      {brand.backgroundImageUrl && hasInfo && (
        <div className="mt-3 flex flex-wrap items-center gap-2.5 text-sm text-neutral-600">
          {discountRate > 0 && minOrder > 0 && (
            <>
              <span className="rounded bg-neutral-900 px-1.5 py-0.5 text-xs font-medium text-white">
                할인
              </span>
              <span>
                {minOrder.toLocaleString()}원 이상{" "}
                <span className="font-bold text-neutral-900">{Math.floor(discountRate)}%</span>
              </span>
            </>
          )}
          {discountRate > 0 && minOrder > 0 && brand.basicShippingFee > 0 && (
            <span className="text-neutral-300">|</span>
          )}
          {brand.basicShippingFee > 0 && (
            <span>배송비 {brand.basicShippingFee.toLocaleString()}원</span>
          )}
        </div>
      )}
    </div>
  );
}

export function BrandContent({ brandId }: BrandContentProps) {
  const { isAuthenticated } = useAuth();
  const searchParams = useSearchParams();
  const router = useRouter();

  const page = Number(searchParams.get("page") ?? "1");
  const order = searchParams.get("order") ?? "best";
  const search = searchParams.get("search") ?? "";

  const { data, isLoading, isError } = useBrandProducts({
    idBrand: brandId,
    page,
    order,
    search,
  });

  const [brandSearch, setBrandSearch] = useState(search);
  const priceRange = searchParams.get("price") ?? "";
  const allProducts: ProductListItem[] = data?.data?.list ?? [];
  const totalPages = data?.data?.pagination?.total_page ?? 1;
  const totalCount = data?.data?.pagination?.data_count ?? 0;

  const { navigateToPage, renderPageNumbers } = usePagination({
    page,
    totalPages,
    searchParams,
    router,
  });

  useEffect(() => {
    setBrandSearch(search);
  }, [search]);

  // Client-side price filter. Text search is handled by the backend so pagination
  // stays consistent across the full brand result set.
  const filteredProducts = allProducts.filter((p) => {
    if (priceRange) {
      const [min, max] = priceRange.split("-").map(Number);
      if (p.goodsPrice < min) return false;
      if (max && p.goodsPrice > max) return false;
    }
    return true;
  });

  // Client-side sort fallback (backend sort columns may lack meaningful data)
  const products = (() => {
    switch (order) {
      case "price_asc":
        return [...filteredProducts].sort((a, b) => a.goodsPrice - b.goodsPrice);
      case "price_desc":
        return [...filteredProducts].sort((a, b) => b.goodsPrice - a.goodsPrice);
      case "latest":
        return [...filteredProducts].sort((a, b) => b.id - a.id);
      default:
        return filteredProducts;
    }
  })();

  const sortOptions = isAuthenticated
    ? SORT_OPTIONS
    : SORT_OPTIONS.filter((o) => !PRICE_SORT_VALUES.has(o.value));

  function updateParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams.toString());
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    // 정렬 변경 시 페이지 리셋
    if (key === "order") next.delete("page");
    router.push("?" + next.toString());
  }

  function submitBrandSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = new URLSearchParams(searchParams.toString());
    const normalizedSearch = brandSearch.trim();
    if (normalizedSearch) {
      next.set("search", normalizedSearch);
    } else {
      next.delete("search");
    }
    next.delete("page");
    router.push("?" + next.toString());
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <BrandProfile brandId={brandId} />

      {/* Price filter + search row */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* Price filter chips — 로그인 시에만 노출 (비로그인은 가격 자체가 숨김) */}
        {isAuthenticated && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => updateParam("price", null)}
              className={cn(
                "px-3 py-1.5 text-[13px] rounded-sm border transition-colors",
                !priceRange
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400",
              )}
            >
              전체
            </button>
            {PRICE_RANGES.map((range) => (
              <button
                key={range.value}
                type="button"
                onClick={() =>
                  updateParam("price", priceRange === range.value ? null : range.value)
                }
                className={cn(
                  "px-3 py-1.5 text-[13px] rounded-sm border transition-colors",
                  priceRange === range.value
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400",
                )}
              >
                {range.label}
              </button>
            ))}
          </div>
        )}

        {/* Brand search */}
        <form className="relative flex-1 max-w-sm" onSubmit={submitBrandSearch}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={brandSearch}
            onChange={(e) => setBrandSearch(e.target.value)}
            placeholder="브랜드 내 상품 검색"
            className="h-9 rounded-sm pl-9 text-sm"
          />
        </form>
      </div>

      {/* 헤더: 상품 수 + 정렬 */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">
          {isLoading ? "" : search ? `${totalCount}개의 검색 결과` : `${totalCount}개의 상품`}
        </p>
        <Select value={order} onValueChange={(val) => updateParam("order", val)}>
          <SelectTrigger className="w-[140px] h-9 text-[13px] rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            {sortOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-[13px]">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 상품 그리드 */}
      {isLoading ? (
        <ProductListSkeleton count={8} />
      ) : isError ? (
        <EmptyState
          icon={PackageOpen}
          title="브랜드 정보를 불러올 수 없습니다"
          description="잠시 후 다시 시도해주세요."
          blurColor="neutral-100"
          iconColor="text-neutral-300"
          action={{ label: "다시 시도", onClick: () => window.location.reload() }}
        />
      ) : products.length === 0 ? (
        <EmptyState
          icon={PackageOpen}
          title="등록된 상품이 없습니다"
          description="이 브랜드에 아직 등록된 상품이 없습니다."
          blurColor="neutral-50"
          iconColor="text-neutral-200"
          action={{ label: "상품 둘러보기", href: "/shopping" }}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} priority={i < 8} />
            ))}
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Pagination className="mt-8">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => navigateToPage(Math.max(1, page - 1))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                {renderPageNumbers()}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => navigateToPage(Math.min(totalPages, page + 1))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
